<!-- management officeやoperation officeを元にアイテムを出し分けしたい場合に使用する。 -->

<template>
  <component
    :is="isCombobox ? VCombobox : VAutocomplete"
    :label="label"
    v-model="modelValue"
    v-model:search="searchText"
    @blur="emitBlur"
    :custom-filter="() => true"
    :items="filterItems"
    :item-title="itemName"
    :item-value="itemName"
    :return-object="!isCombobox"
    :readonly="readonly"
    :disabled="disabled"
    :error-messages="props.errorMessages"
    :clearable="props.clearable"
  />
</template>
<script setup lang="ts">
import getManagementOffice from '@/types/responses/getManagementOffice';
import getOperationOffice from '@/types/responses/getOperationOffice';
import { watch, computed, ref } from 'vue';
import { VAutocomplete, VCombobox } from 'vuetify/components';

const props = withDefaults(
  defineProps<{
    modelValue: any;
    label: string;
    itemName: string; // 表示する項目名
    items: any[];
    initialItemId?: number;
    managementOffice?: getManagementOffice | undefined | null;
    operationOffices?: getOperationOffice[] | undefined | null;
    errorMessages?: string[];
    readonly?: boolean;
    disabled?: boolean;
    selectFirstIfOnlyOne?: boolean;
    clearable?: boolean;
    isCombobox?: boolean;
    mapToObject?: (value: string) => any;
  }>(),
  {
    disabled: false,
    clearable: true,
    isCombobox: false,
  }
)

const emit = defineEmits(['update:modelValue', 'blur']);

const searchText = ref('');

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (props.isCombobox && typeof value === 'string' && props.mapToObject) {
      emit('update:modelValue', props.mapToObject(value));
    } else {
      emit("update:modelValue", value);
    }
  },
})

// operationOffice
// managementOffice
// items
// が変更された時にdisplayItemsを更新する
watch(
  () => [props.operationOffices, props.managementOffice, props.items],
  () => {
    setInitialItem();
    checkIncludesItems()
  }
);

watch(() => props.initialItemId,() => {
  setInitialItem()
});

const normalize = (text: any): string => {
  const str = typeof text === 'string' ? text : typeof text?.title === 'string' ? text.title : ''
  return str
    .replace(/\s/g, '')
    .replace(/\u3000/g, '')
    .toLowerCase()
}

const checkMatch = (itemText: string, queryText: string) => {
  return normalize(itemText).includes(normalize(queryText))
}

// 権限によって絞り込んだitems。検索文字には依存しない。
const permittedItems = computed(() => {
  let result = props.items;
  if (props.managementOffice !== undefined && props.managementOffice !== null) {
    result = result.filter(
      (item) =>
        item.managementOfficeId === props.managementOffice?.id &&
        item.managementOfficeType === props.managementOffice?.type // management officeのIDは重複する可能性があるので、typeで絞り込む。
    );
  }

  if (
    props.operationOffices !== undefined &&
    props.operationOffices !== null &&
    props.operationOffices.length &&
    props.operationOffices[0]
  ) {
    result = result.filter((item) => {
      return props.operationOffices?.some((office) => {
        return office.id === item?.operationOfficeId
      })
    });
  }

  return result;
});

const filterItems = computed(() => {
  let result = permittedItems.value;

  // 検索文字でitemsを絞り込む
  let shouldFilter = !!searchText.value;

  if (shouldFilter && !props.isCombobox && props.modelValue) {
    const selectedItemLabel = props.modelValue[props.itemName];
    // v-autocompleteかつ、検索文字列が、現在選択されている項目の表示名と完全に一致している場合はフィルタリングをスキップ（全件表示）
    if (searchText.value === selectedItemLabel) {
      shouldFilter = false;
    }
  }

  if (shouldFilter) {
    result = result.filter(item => {
      const itemText = item[props.itemName];
      return checkMatch(itemText, searchText.value);
    });
  }

  // selectFirstIfOnlyOneがtrueの場合、displayItemsが1つの場合は選択する
  if (props.selectFirstIfOnlyOne && result.length === 1) {
    setSelectedItem(result.at(0));
  }

  return result
});

const setInitialItem = () => {
  if (props.initialItemId !== undefined) {
    // 初期値の選択は検索文字に左右されないよう、権限で絞り込んだitemsから探す。
    modelValue.value = permittedItems.value.find((item) => {
      return item.id === props.initialItemId
    });
  }
};

const setSelectedItem = (result: any) => {
  modelValue.value = result;
};

const checkIncludesItems = () => {
  if (!props.modelValue) {
    return;
  }
  if (!props.items || props.items.length === 0) {
    return;
  }
  if (props.isCombobox) {
    return;
  }
  // 空オブジェクトはどの項目にも対応しない値なので、未選択に戻す。
  if (Object.keys(props.modelValue).length === 0) {
    modelValue.value = null;
    return;
  }
  // modelValueの各項目がitemと一致するかで判定する。
  const isIncluded = permittedItems.value.some((item) =>
    Object.keys(props.modelValue).every(
      (key) => JSON.stringify(item[key]) === JSON.stringify(props.modelValue[key])
    )
  );
  if (!isIncluded) {
    modelValue.value = null;
  }
};

const emitBlur = () => {
  emit('blur');
};
</script>
